import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ProductFormDialog } from "@/pages/admin/ProductFormDialog";
import { api, brl, imgUrl, priceSuffix, formatError } from "@/lib/api";

function ResellerCell({ p, onToggle }) {
  const special = p.reseller_price;
  const value = special || (p.reseller_discount ? Math.round(p.price * 50) / 100 : null);
  return (
    <TableCell>
      <label className="flex cursor-pointer items-center gap-2">
        <Checkbox checked={!!p.reseller_discount} onCheckedChange={(v) => onToggle(p, v === true, "reseller")} className="data-[state=checked]:border-violet-600 data-[state=checked]:bg-violet-600" data-testid={`checkbox-reseller-discount-${p.id}`} aria-label={`50% para revendedores em ${p.name}`} />
        <span className="text-xs font-semibold text-slate-600">50%</span>
      </label>
      <div className="mt-1 whitespace-nowrap font-mono text-xs text-violet-700" data-testid={`admin-product-reseller-${p.id}`}>
        {value ? <>{brl(value)}<span className="text-slate-400">{priceSuffix(p)}</span>{special && <span className="ml-1 font-sans text-[10px] text-slate-500">preço especial</span>}</> : !p.price_table?.reseller_prices && <span className="text-slate-400">—</span>}
      </div>
      {p.price_table?.reseller_prices && <div className="font-sans text-[10px] font-semibold text-violet-700" data-testid={`admin-product-reseller-table-${p.id}`}>preço de revenda nos pacotes</div>}
    </TableCell>
  );
}

function VipCell({ p, onToggle }) {
  const value = p.vip_discount ? Math.round(p.price * 90) / 100 : null;
  return (
    <TableCell>
      <label className="flex cursor-pointer items-center gap-2">
        <Checkbox checked={!!p.vip_discount} onCheckedChange={(v) => onToggle(p, v === true, "vip")} className="data-[state=checked]:border-amber-500 data-[state=checked]:bg-amber-500" data-testid={`checkbox-vip-discount-${p.id}`} aria-label={`10% para clientes VIP em ${p.name}`} />
        <span className="text-xs font-semibold text-slate-600">10%</span>
      </label>
      <div className="mt-1 whitespace-nowrap font-mono text-xs text-amber-700" data-testid={`admin-product-vip-${p.id}`}>
        {value ? <>{p.price_table && <span className="font-sans text-[10px] text-slate-500">a partir de </span>}{brl(value)}<span className="text-slate-400">{priceSuffix(p)}</span></> : <span className="text-slate-400">—</span>}
      </div>
    </TableCell>
  );
}

const DISCOUNT_MSG = {
  reseller: ["50% para revendedores", "desconto de revendedor removido"],
  vip: ["10% para clientes VIP", "desconto VIP removido"],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const toggleDiscount = async (p, enabled, kind) => {
    setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, [`${kind}_discount`]: enabled } : x)));
    try {
      await api.patch(`/admin/products/${p.id}/${kind}-discount`, { enabled });
      toast.success(`${p.name}: ${DISCOUNT_MSG[kind][enabled ? 0 : 1]}`);
    } catch (e) {
      toast.error(formatError(e));
      load();
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleting.id}`);
      toast.success("Produto excluído");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
    setDeleting(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Produtos</h1>
          <p className="text-slate-500">Cadastre produtos, imagens e preços por m² ou por unidade.</p>
          <p className="mt-1 text-sm text-violet-700" data-testid="reseller-discount-hint">Marque a coluna Revendedor para dar 50% de desconto a todos os revendedores. Se o produto tiver preço especial de revenda, vale o preço especial.</p>
          <p className="text-sm text-amber-700" data-testid="vip-discount-hint">Marque a coluna Cliente VIP para dar 10% de desconto aos clientes VIP (vale também para os pacotes da tabela de preços).</p>
        </div>
        <Button onClick={() => setEditing({})} className="rounded-full bg-[#0099DD] hover:bg-sky-700" data-testid="btn-new-product"><Plus className="h-4 w-4" /> Novo produto</Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <Table data-testid="admin-products-table">
          <TableHeader>
            <TableRow><TableHead>Produto</TableHead><TableHead>Categoria</TableHead><TableHead>Venda</TableHead><TableHead>Preço</TableHead><TableHead>Revendedor</TableHead><TableHead>Cliente VIP</TableHead><TableHead>Status</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                <TableCell><div className="flex items-center gap-3"><img src={imgUrl(p.image_url)} alt="" className="h-10 w-10 rounded object-cover bg-slate-100" /><span className="font-semibold">{p.name}</span></div></TableCell>
                <TableCell className="text-slate-500">{p.category}</TableCell>
                <TableCell>{p.pricing_type === "m2" ? "Por m²" : "Por unidade"}</TableCell>
                <TableCell className="font-mono">{p.price_table && <span className="block font-sans text-[10px] font-semibold uppercase text-sky-700" data-testid={`admin-product-table-${p.id}`}>Tabela · a partir de</span>}{brl(p.price)}<span className="text-xs text-slate-400">{priceSuffix(p)}</span></TableCell>
                <ResellerCell p={p} onToggle={toggleDiscount} />
                <VipCell p={p} onToggle={toggleDiscount} />
                <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${p.active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Ativo" : "Inativo"}</span></TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="icon" onClick={() => setEditing(p)} data-testid={`btn-edit-product-${p.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleting(p)} data-testid={`btn-delete-product-${p.id}`} aria-label="Excluir"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {editing && <ProductFormDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.name}" será removido da loja. Pedidos já feitos não são afetados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
